import React, { useState, useEffect } from 'react';
import api from '../../services/api';
import { Button } from '../FoodPremisesStyles';
import styled from 'styled-components';
import { FaEdit, FaTrash, FaPlus, FaEye, FaCheck, FaTimes, FaUpload, FaFilePdf } from 'react-icons/fa';

const Container = styled.div`
  padding: 24px;
`;

const TabNav = styled.div`
  display: flex; gap: 16px; margin-bottom: 24px; border-bottom: 1px solid #ddd;
`;

const Tab = styled.button`
  padding: 12px 24px; background: none; border: none; border-bottom: 3px solid transparent;
  font-weight: 600; color: #666; cursor: pointer; font-size: 16px;
  ${({ $active }) => $active && `
    border-bottom-color: #16a34a; color: #16a34a;
  `}
`;

const Card = styled.div`
  background: #fff; border-radius: 8px; padding: 24px; box-shadow: 0 1px 3px rgba(0,0,0,0.1); margin-bottom: 24px;
`;

const Input = styled.input`
  width: 100%; padding: 10px; margin-bottom: 12px; border: 1px solid #ddd; border-radius: 4px;
`;

const Textarea = styled.textarea`
  width: 100%; padding: 10px; margin-bottom: 12px; border: 1px solid #ddd; border-radius: 4px; min-height: 80px;
`;

const Table = styled.table`
  width: 100%; border-collapse: collapse;
  th, td { padding: 12px; text-align: left; border-bottom: 1px solid #eee; }
  th { background: #f9fafb; font-weight: 600; }
`;

const Badge = styled.span`
  padding: 4px 8px; border-radius: 999px; font-size: 12px; font-weight: 600;
  background: ${({ status }) => {
    switch(status) {
      case 'approved': return '#dcfce7';
      case 'rejected': return '#fee2e2';
      default: return '#fef9c3';
    }
  }};
  color: ${({ status }) => {
    switch(status) {
      case 'approved': return '#166534';
      case 'rejected': return '#991b1b';
      default: return '#854d0e';
    }
  }};
`;

const ModalOverlay = styled.div`
  position: fixed; top:0; left:0; right:0; bottom:0; background: rgba(0,0,0,0.5);
  display: flex; align-items: center; justify-content: center; z-index: 1000;
`;

const ModalContent = styled.div`
  background: #fff; padding: 32px; border-radius: 12px; width: 90%; max-width: 600px; max-height: 90vh; overflow-y: auto;
`;

const toBase64 = file => new Promise((resolve, reject) => {
  const reader = new FileReader();
  reader.readAsDataURL(file);
  reader.onload = () => resolve(reader.result);
  reader.onerror = error => reject(error);
});

const FoodPremisesCMS = () => {
  const [activeTab, setActiveTab] = useState('applications');
  const [content, setContent] = useState({ header: {}, foodLaws: [], feeSchedule: [] });
  const [applications, setApplications] = useState([]);
  const [selectedApp, setSelectedApp] = useState(null);
  const [reviewCert, setReviewCert] = useState('');
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [newAccount, setNewAccount] = useState({ username: '', password: '' });
  const [accountMessage, setAccountMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const contentData = await api.foodPremises.getContent();
      setContent(contentData || { header: {}, foodLaws: [], feeSchedule: [] });
      const appsData = await api.foodPremises.getAllApplications();
      setApplications(appsData || []);
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreateAccount = async () => {
    if (!newAccount.username || !newAccount.password) {
      setAccountMessage('Username and password required');
      return;
    }
    setAccountMessage('');
    try {
      await api.foodPremises.createAccount(newAccount);
      setAccountMessage('Business account created successfully.');
      setNewAccount({ username: '', password: '' });
    } catch (error) {
      setAccountMessage(error.message || 'Failed to create account');
    }
  };

  // Content Handlers
  const handleHeaderChange = (e) => {
    setContent({ ...content, header: { ...content.header, [e.target.name]: e.target.value } });
  };

  const handleHeaderImage = async (e) => {
    if (e.target.files[0]) {
      const base64 = await toBase64(e.target.files[0]);
      setContent({ ...content, header: { ...content.header, image: base64 } });
    }
  };

  const handleSaveContent = async () => {
    setSubmitting(true);
    try {
      await api.foodPremises.updateContent(content);
      alert('Content updated successfully');
    } catch (error) {
      alert('Failed to update content');
    } finally {
      setSubmitting(false);
    }
  };

  // Section Helpers (Laws/Fees)
  const addSection = (type) => {
    setContent({ ...content, [type]: [...(content[type] || []), { title: '', content: '', pdfFile: '' }] });
  };

  const updateSection = (type, index, field, value) => {
    const newSections = [...content[type]];
    newSections[index][field] = value;
    setContent({ ...content, [type]: newSections });
  };

  const removeSection = (type, index) => {
    const newSections = [...content[type]];
    newSections.splice(index, 1);
    setContent({ ...content, [type]: newSections });
  };

  const handlePdfUpload = async (type, index, file) => {
    if (file) {
      const base64 = await toBase64(file);
      updateSection(type, index, 'pdfFile', base64);
    }
  };

  // Application Handlers
  const openReview = (app) => {
    setSelectedApp(app);
    setReviewCert(app.certificate || '');
  };

  const handleReviewSubmit = async (status) => {
    if (status === 'approved' && !reviewCert) {
      if (!window.confirm('Approve without uploading a certificate?')) return;
    }
    setSubmitting(true);
    try {
      await api.foodPremises.reviewApplication(selectedApp._id, { status, certificate: reviewCert });
      setSelectedApp(null);
      fetchData();
    } catch (error) {
      alert('Failed to update application');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCertUpload = async (e) => {
    if (e.target.files[0]) {
      const base64 = await toBase64(e.target.files[0]);
      setReviewCert(base64);
    }
  };

  return (
    <Container>
      <h1>Food Premises Management</h1>
      
      <TabNav>
        <Tab $active={activeTab === 'applications'} onClick={() => setActiveTab('applications')}>Applications</Tab>
        <Tab $active={activeTab === 'content'} onClick={() => setActiveTab('content')}>Page Content</Tab>
      </TabNav>

      {activeTab === 'applications' && (
        <Card>
          <Table>
            <thead>
              <tr>
                <th>Premises Name</th>
                <th>Owner</th>
                <th>Contact</th>
                <th>Status</th>
                <th>Date</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {applications.map(app => (
                <tr key={app._id}>
                  <td>{app.premisesName}</td>
                  <td>{app.businessOwnerName}</td>
                  <td>{app.contactNumbers}</td>
                  <td><Badge status={app.status}>{app.status}</Badge></td>
                  <td>{new Date(app.createdAt).toLocaleDateString()}</td>
                  <td>
                    <Button style={{ padding: '6px 12px', fontSize: 12 }} onClick={() => openReview(app)}>Review</Button>
                  </td>
                </tr>
              ))}
              {applications.length === 0 && <tr><td colSpan="6">No applications found.</td></tr>}
            </tbody>
          </Table>
        </Card>
      )}

      {activeTab === 'content' && (
        <div>
          <Card>
            <h3>Header Configuration</h3>
            <Input name="title" value={content.header?.title || ''} onChange={handleHeaderChange} placeholder="Page Title" />
            <Textarea name="description" value={content.header?.description || ''} onChange={handleHeaderChange} placeholder="Page Description" />
            <div style={{ marginBottom: 12 }}>
              <label>Header Image: </label>
              <input type="file" onChange={handleHeaderImage} accept="image/*" />
            </div>
            {content.header?.image && <img src={content.header.image} alt="Header" style={{ height: 100 }} />}
          </Card>

          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3>Food Laws Sections</h3>
              <Button onClick={() => addSection('foodLaws')}><FaPlus /> Add Section</Button>
            </div>
            {content.foodLaws?.map((law, index) => (
              <div key={index} style={{ border: '1px solid #eee', padding: 16, marginBottom: 16, borderRadius: 8 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <Input value={law.title} onChange={(e) => updateSection('foodLaws', index, 'title', e.target.value)} placeholder="Section Title" />
                  <Button style={{ background: '#dc2626', padding: '0 12px' }} onClick={() => removeSection('foodLaws', index)}><FaTrash /></Button>
                </div>
                <Textarea value={law.content} onChange={(e) => updateSection('foodLaws', index, 'content', e.target.value)} placeholder="Content (HTML supported)" />
                <div>
                  <label>Upload PDF: </label>
                  <input type="file" onChange={(e) => handlePdfUpload('foodLaws', index, e.target.files[0])} accept="application/pdf" />
                  {law.pdfFile && <span style={{ color: 'green', marginLeft: 8 }}><FaCheck /> PDF Attached</span>}
                </div>
              </div>
            ))}
          </Card>

          <Card>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
              <h3>Fee Schedule Sections</h3>
              <Button onClick={() => addSection('feeSchedule')}><FaPlus /> Add Section</Button>
            </div>
            {content.feeSchedule?.map((item, index) => (
              <div key={index} style={{ border: '1px solid #eee', padding: 16, marginBottom: 16, borderRadius: 8 }}>
                <div style={{ display: 'flex', gap: 8, marginBottom: 8 }}>
                  <Input value={item.title} onChange={(e) => updateSection('feeSchedule', index, 'title', e.target.value)} placeholder="Item Title" />
                  <Button style={{ background: '#dc2626', padding: '0 12px' }} onClick={() => removeSection('feeSchedule', index)}><FaTrash /></Button>
                </div>
                <Textarea value={item.content} onChange={(e) => updateSection('feeSchedule', index, 'content', e.target.value)} placeholder="Details" />
                <div>
                  <label>Upload PDF: </label>
                  <input type="file" onChange={(e) => handlePdfUpload('feeSchedule', index, e.target.files[0])} accept="application/pdf" />
                  {item.pdfFile && <span style={{ color: 'green', marginLeft: 8 }}><FaCheck /> PDF Attached</span>}
                </div>
              </div>
            ))}
          </Card>

          <Button onClick={handleSaveContent} style={{ width: '100%', padding: 16 }} disabled={submitting}>
            {submitting ? 'Saving...' : 'Save All Content Changes'}
          </Button>
        </div>
      )}

      {selectedApp && (
        <ModalOverlay onClick={() => setSelectedApp(null)}>
          <ModalContent onClick={e => e.stopPropagation()}>
            <h2>Review Application</h2>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 24 }}>
              <div><strong>Premises:</strong> {selectedApp.premisesName}</div>
              <div><strong>Owner:</strong> {selectedApp.businessOwnerName}</div>
              <div><strong>Contact:</strong> {selectedApp.contactNumbers}</div>
              <div><strong>Email:</strong> {selectedApp.email}</div>
              <div style={{ gridColumn: '1/-1' }}><strong>Address:</strong> {selectedApp.physicalAddress}</div>
              
              <div><strong>County:</strong> {selectedApp.county}</div>
              <div><strong>District:</strong> {selectedApp.district}</div>
              
              <div style={{ gridColumn: '1/-1' }}><strong>Type of Premises:</strong> {selectedApp.typeOfPremises?.join(', ')}</div>
              
              <div style={{ gridColumn: '1/-1' }}>
                <strong>Product Categories:</strong> {selectedApp.productCategories?.join(', ')}
                {selectedApp.otherProductCategory && <span> (Other: {selectedApp.otherProductCategory})</span>}
              </div>
              
              <div><strong>Employees:</strong> {selectedApp.numberOfEmployees}</div>
              <div><strong>Registration No:</strong> {selectedApp.businessRegistrationNumber}</div>
              
              <div><strong>FAL Code:</strong> {selectedApp.falPremisesCode}</div>
              <div><strong>Date of Listing:</strong> {selectedApp.dateOfListing}</div>
            </div>

            <div style={{ marginBottom: 24 }}>
              <label style={{ display: 'block', marginBottom: 8, fontWeight: '600' }}>Upload Certificate (Image/PDF)</label>
              <input type="file" onChange={handleCertUpload} accept="image/*,application/pdf" />
              {reviewCert && <div style={{ marginTop: 8, color: 'green' }}><FaCheck /> Certificate ready to be issued</div>}
            </div>

            <div style={{ display: 'flex', gap: 16, justifyContent: 'flex-end' }}>
              <Button style={{ background: '#9ca3af' }} onClick={() => setSelectedApp(null)} disabled={submitting}>Cancel</Button>
              <Button style={{ background: '#dc2626' }} onClick={() => handleReviewSubmit('rejected')} disabled={submitting}>{submitting ? 'Processing...' : 'Reject'}</Button>
              <Button style={{ background: '#16a34a' }} onClick={() => handleReviewSubmit('approved')} disabled={submitting}>{submitting ? 'Processing...' : 'Approve & Issue'}</Button>
            </div>
          </ModalContent>
        </ModalOverlay>
      )}
    </Container>
  );
};

export default FoodPremisesCMS;
